import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Tooltip from "@mui/material/Tooltip";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import Langs, { Localize } from "../../../../app/lang/langs";

const ORANGE = "#FF9015";
const NAVY = "#002880";
const ALERT = "#fb6962";
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

/**
 * Dashboard notification button for V-Sécurité (awareness + quiz).
 *
 * Shows a shield icon next to the bell, with a pulsing badge + ping ring
 * when the user has pending awareness campaigns or unfinished quizzes.
 * Clicking opens a popover summarising the pending items, each clickable
 * to jump straight into V-Sécurité.
 */
export default function DashboardVSecuriteNotificationsButton() {
    const navigate = useNavigate();
    const lang = useSelector((s) => s.settings.lang);
    const pendingAwareness = useSelector((s) => s.vsecurite?.awareness?.collabPending) || [];
    const pendingQuizzes = useSelector((s) => s.vsecurite?.quizzes?.collabPending) || [];
    const selectedTeamsIds = useSelector((s) => s.teams?.selectedTeamsIds) || [];
    const myTeam = useSelector((s) => s.teams?.myTeam);

    const totalCount = pendingAwareness.length + pendingQuizzes.length;
    const hasPending = totalCount > 0;

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    /** Navigate to the V-Sécurité view, preserving the active team id. */
    const goToVSecurite = () => {
        const teamId = selectedTeamsIds[0] || myTeam?.id || 0;
        handleClose();
        navigate(`/vsecurite/${teamId}/0`);
    };

    /* Cap each section preview to keep the popover compact */
    const MAX_PREVIEW = 4;
    const awarenessPreview = pendingAwareness.slice(0, MAX_PREVIEW);
    const quizPreview = pendingQuizzes.slice(0, MAX_PREVIEW);
    const awarenessExtra = Math.max(0, pendingAwareness.length - MAX_PREVIEW);
    const quizExtra = Math.max(0, pendingQuizzes.length - MAX_PREVIEW);

    return (
        <>
            <Tooltip
                title={Localize("VSEC_NOTIF_TOOLTIP", lang)}
                slotProps={{
                    tooltip: {
                        sx: {
                            borderRadius: "10px",
                            backgroundColor: "rgba(0, 40, 128, 0.88)",
                            backdropFilter: "blur(8px)",
                            WebkitBackdropFilter: "blur(8px)",
                            fontWeight: 400,
                            letterSpacing: "0.02em",
                            px: 1.5,
                            py: 0.7,
                            fontSize: "0.8rem",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        display: "inline-flex",
                        /* Pulsing ring — only when there are pending items */
                        "&::before": hasPending
                            ? {
                                  content: '""',
                                  position: "absolute",
                                  inset: -2,
                                  borderRadius: "14px",
                                  border: `2px solid ${ALERT}`,
                                  opacity: 0.6,
                                  animation: "vsecPing 1.6s ease-out infinite",
                                  pointerEvents: "none",
                              }
                            : {},
                        "@keyframes vsecPing": {
                            "0%": { transform: "scale(1)", opacity: 0.6 },
                            "80%, 100%": { transform: "scale(1.35)", opacity: 0 },
                        },
                        "@keyframes vsecShake": {
                            "0%, 90%, 100%": { transform: "rotate(0deg)" },
                            "92%": { transform: "rotate(-10deg)" },
                            "94%": { transform: "rotate(8deg)" },
                            "96%": { transform: "rotate(-6deg)" },
                            "98%": { transform: "rotate(4deg)" },
                        },
                    }}
                >
                    <IconButton
                        aria-label="vsecurite-notifications"
                        onClick={handleOpen}
                        sx={{
                            borderRadius: "12px",
                            border: hasPending
                                ? `1px solid ${ALERT}`
                                : "1px solid rgba(255, 144, 21, 0.18)",
                            backgroundColor: hasPending
                                ? "rgba(251, 105, 98, 0.08)"
                                : "rgba(255, 255, 255, 0.7)",
                            backdropFilter: "blur(12px)",
                            WebkitBackdropFilter: "blur(12px)",
                            boxShadow: hasPending
                                ? "0 4px 16px rgba(251, 105, 98, 0.18), 0 1px 3px rgba(0, 40, 128, 0.04)"
                                : "0 4px 16px rgba(255, 144, 21, 0.08), 0 1px 3px rgba(0, 40, 128, 0.04)",
                            transition: `all 0.25s ${EASE}`,
                            "&:hover": {
                                transform: "scale(1.05)",
                                backgroundColor: hasPending
                                    ? "rgba(251, 105, 98, 0.14)"
                                    : "rgba(255, 144, 21, 0.08)",
                                boxShadow:
                                    "0 8px 24px rgba(255, 144, 21, 0.16), 0 2px 6px rgba(0, 40, 128, 0.06)",
                                border: hasPending
                                    ? `1px solid ${ALERT}`
                                    : "1px solid rgba(255, 144, 21, 0.32)",
                            },
                            "&:active": { transform: "scale(0.97)" },
                        }}
                    >
                        <Badge
                            color="error"
                            badgeContent={totalCount || 0}
                            invisible={!hasPending}
                            sx={{
                                "& .MuiBadge-badge": {
                                    fontWeight: 700,
                                    fontSize: "0.7rem",
                                    minWidth: 18,
                                    height: 18,
                                    borderRadius: "9px",
                                    boxShadow: hasPending
                                        ? "0 2px 8px rgba(251, 105, 98, 0.45)"
                                        : "none",
                                    background: hasPending
                                        ? `linear-gradient(135deg, ${ALERT}, ${ORANGE})`
                                        : undefined,
                                    animation: hasPending
                                        ? "badgePulse 1.8s ease-in-out infinite"
                                        : "none",
                                },
                                "@keyframes badgePulse": {
                                    /* Preserve MUI's default translate(50%, -50%) so the badge
                                       stays on the icon's top-right corner while pulsing. */
                                    "0%, 100%": {
                                        transform: "translate(50%, -50%) scale(1)",
                                        boxShadow: "0 2px 8px rgba(251, 105, 98, 0.45)",
                                    },
                                    "50%": {
                                        transform: "translate(50%, -50%) scale(1.18)",
                                        boxShadow: "0 2px 16px rgba(251, 105, 98, 0.7)",
                                    },
                                },
                            }}
                        >
                            <ShieldOutlinedIcon
                                sx={{
                                    color: hasPending ? ALERT : NAVY,
                                    fontSize: 22,
                                    transition: `color 0.25s ${EASE}`,
                                    /* Subtle wobble every ~6s when pending — draws the eye */
                                    animation: hasPending
                                        ? "vsecShake 6s ease-in-out infinite"
                                        : "none",
                                    transformOrigin: "50% 80%",
                                }}
                            />
                        </Badge>
                    </IconButton>
                </Box>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            width: 360,
                            maxWidth: "calc(100vw - 24px)",
                            borderRadius: "16px",
                            border: "1px solid rgba(255, 144, 21, 0.12)",
                            backgroundColor: "rgba(255, 255, 255, 0.92)",
                            backdropFilter: "blur(20px)",
                            WebkitBackdropFilter: "blur(20px)",
                            boxShadow:
                                "0 16px 48px rgba(0, 40, 128, 0.12), 0 4px 16px rgba(255, 144, 21, 0.08)",
                            overflow: "hidden",
                        },
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        background:
                            "linear-gradient(135deg, rgba(255, 144, 21, 0.08), rgba(0, 40, 128, 0.04))",
                        borderBottom: "1px solid rgba(255, 144, 21, 0.10)",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <ShieldOutlinedIcon sx={{ color: ORANGE, fontSize: 20 }} />
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: "0.95rem",
                            color: NAVY,
                            letterSpacing: "-0.01em",
                            flex: 1,
                        }}
                    >
                        <Langs str="VSEC_NOTIF_HEADER" />
                    </Typography>
                    {hasPending && (
                        <Box
                            sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: "999px",
                                background: `linear-gradient(135deg, ${ALERT}, ${ORANGE})`,
                                color: "#fff",
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                minWidth: 22,
                                textAlign: "center",
                            }}
                        >
                            {totalCount}
                        </Box>
                    )}
                </Box>

                {/* Empty state */}
                {!hasPending && (
                    <Box sx={{ px: 2.5, py: 4, textAlign: "center" }}>
                        <CheckCircleOutlineIcon
                            sx={{ fontSize: 40, color: "#4c8c2b", opacity: 0.7, mb: 1 }}
                        />
                        <Typography sx={{ fontSize: "0.88rem", color: "rgba(0,0,0,0.55)" }}>
                            <Langs str="VSEC_NOTIF_EMPTY" />
                        </Typography>
                    </Box>
                )}

                {/* Awareness section */}
                {pendingAwareness.length > 0 && (
                    <Box sx={{ pt: 1 }}>
                        <SectionHeader
                            icon={<ArticleOutlinedIcon sx={{ fontSize: 16, color: NAVY }} />}
                            label={<Langs str="VSEC_NOTIF_AWARENESS_SECTION" />}
                            count={pendingAwareness.length}
                        />
                        <Stack>
                            {awarenessPreview.map((c) => (
                                <PendingItem
                                    key={`a-${c.id}`}
                                    title={c.title}
                                    subtitle={c.description}
                                    onClick={goToVSecurite}
                                />
                            ))}
                            {awarenessExtra > 0 && (
                                <MoreLine
                                    count={awarenessExtra}
                                    onClick={goToVSecurite}
                                    lang={lang}
                                />
                            )}
                        </Stack>
                    </Box>
                )}

                {pendingAwareness.length > 0 && pendingQuizzes.length > 0 && (
                    <Divider sx={{ my: 0.5, borderColor: "rgba(0,0,0,0.04)" }} />
                )}

                {/* Quiz section */}
                {pendingQuizzes.length > 0 && (
                    <Box sx={{ pt: pendingAwareness.length > 0 ? 0 : 1 }}>
                        <SectionHeader
                            icon={<QuizOutlinedIcon sx={{ fontSize: 16, color: NAVY }} />}
                            label={<Langs str="VSEC_NOTIF_QUIZ_SECTION" />}
                            count={pendingQuizzes.length}
                        />
                        <Stack>
                            {quizPreview.map((q) => (
                                <PendingItem
                                    key={`q-${q.id}`}
                                    title={q.title}
                                    subtitle={q.description}
                                    onClick={goToVSecurite}
                                />
                            ))}
                            {quizExtra > 0 && (
                                <MoreLine
                                    count={quizExtra}
                                    onClick={goToVSecurite}
                                    lang={lang}
                                />
                            )}
                        </Stack>
                    </Box>
                )}

                {/* Footer CTA — always visible */}
                <Divider sx={{ borderColor: "rgba(0,0,0,0.06)" }} />
                <Box sx={{ p: 1.25 }}>
                    <Button
                        fullWidth
                        onClick={goToVSecurite}
                        endIcon={<ChevronRightRoundedIcon />}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            color: ORANGE,
                            borderRadius: "10px",
                            "&:hover": {
                                backgroundColor: "rgba(255, 144, 21, 0.08)",
                            },
                        }}
                    >
                        <Langs str="VSEC_NOTIF_VIEW_ALL" />
                    </Button>
                </Box>
            </Popover>
        </>
    );
}

/* ── Local sub-components ─────────────────────────────────────────────────── */

function SectionHeader({ icon, label, count }) {
    return (
        <Box
            sx={{
                px: 2,
                py: 0.75,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
            }}
        >
            {icon}
            <Typography
                sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "rgba(0, 40, 128, 0.6)",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    flex: 1,
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: ORANGE,
                }}
            >
                {count}
            </Typography>
        </Box>
    );
}

function PendingItem({ title, subtitle, onClick }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                px: 2,
                py: 1,
                cursor: "pointer",
                transition: `background-color 0.2s ${EASE}`,
                "&:hover": {
                    backgroundColor: "rgba(255, 144, 21, 0.06)",
                },
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "rgba(30,30,30,0.9)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {title || "—"}
            </Typography>
            {subtitle && (
                <Typography
                    sx={{
                        fontSize: "0.75rem",
                        color: "rgba(0,0,0,0.5)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
}

function MoreLine({ count, onClick, lang }) {
    const text = (Localize("VSEC_NOTIF_MORE_ITEMS", lang) || "and {count} more…").replace(
        "{count}",
        count
    );
    return (
        <Box
            onClick={onClick}
            sx={{
                px: 2,
                py: 0.75,
                cursor: "pointer",
                fontSize: "0.78rem",
                fontStyle: "italic",
                color: "rgba(0, 40, 128, 0.55)",
                transition: `color 0.2s ${EASE}, background-color 0.2s ${EASE}`,
                "&:hover": {
                    color: ORANGE,
                    backgroundColor: "rgba(255, 144, 21, 0.06)",
                },
            }}
        >
            {text}
        </Box>
    );
}
